// src/features/search/SearchOverlay.jsx
// Mobile full-screen search overlay — hidden on md+.
// Matches the same section structure as SearchDropdown.

import { useEffect, useRef } from 'react';
import { Link }               from 'react-router-dom';
import { Search, X, ArrowRight, RefreshCw, Clock, TrendingUp, Tag, Sparkles } from 'lucide-react';

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="p-4 space-y-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 animate-pulse">
          <div className="w-14 h-14 rounded-md bg-gray-200 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 bg-gray-200 rounded w-4/5" />
            <div className="h-3 bg-gray-200 rounded w-2/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Product row ───────────────────────────────────────────────────────────────
function ProductRow({ product, onClose }) {
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  return (
    <Link
      to={`/product/${product.id}`}
      onClick={onClose}
      className="flex items-center gap-3 px-4 py-3.5 hover:bg-[#FAF8F3] active:bg-[#F5EFE0]
                 border-b border-gray-100 last:border-0 transition-colors"
    >
      <div className="w-14 h-14 rounded-md bg-gray-100 shrink-0 overflow-hidden">
        {product.thumbnail ? (
          <img src={product.thumbnail} alt={product.name}
               className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Search className="w-5 h-5 text-gray-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug">
          {product.name}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-sm font-bold text-[#D4AF37]">
            ₹{Number(product.price).toLocaleString('en-IN')}
          </span>
          {hasDiscount && (
            <span className="text-xs text-gray-400 line-through">
              ₹{Number(product.compare_at_price).toLocaleString('en-IN')}
            </span>
          )}
          {product.discount_percent > 0 && (
            <span className="text-[10px] font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
              {product.discount_percent}% OFF
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHeading({ icon: Icon, label, action }) {
  return (
    <div className="flex items-center justify-between px-4 pt-4 pb-2">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
          {label}
        </span>
      </div>
      {action}
    </div>
  );
}

// ── Chip ──────────────────────────────────────────────────────────────────────
function Chip({ label, onClick, onDelete }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-gray-100
                 hover:bg-[#FAF8F3] text-gray-700 rounded-full transition-colors"
    >
      {label}
      {onDelete && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          onKeyDown={(e) => e.key === 'Enter' && (e.stopPropagation(), onDelete())}
          className="ml-0.5 text-gray-400"
        >
          <X className="w-3.5 h-3.5" />
        </span>
      )}
    </button>
  );
}

// ── SearchOverlay ─────────────────────────────────────────────────────────────
export function SearchOverlay({
  query, setQuery,
  products, categories, suggestions,
  loading, error,
  hasQuery, hasResults,
  isOpen, close, retry, onSubmit, onSelectSuggestion,
  recent, deleteRecent, clearRecent,
  trending,
  didYouMean, similarProducts,
}) {
  const inputRef = useRef(null);

  // Lock body scroll + autofocus
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    setTimeout(() => inputRef.current?.focus(), 100);
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ESC closes
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const showIdle    = !hasQuery;
  const showResults = hasQuery && !loading;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col md:hidden">

      {/* ── Sticky top input bar ── */}
      <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-white shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
            placeholder="Search jewellery…"
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-sm
                       focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
        </div>
        <button
          onClick={close}
          aria-label="Close search"
          className="shrink-0 p-2 text-gray-500 hover:text-gray-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto overscroll-contain">

        {/* ── IDLE: Recent + Trending ── */}
        {showIdle && (
          <>
            {recent.length > 0 && (
              <>
                <SectionHeading
                  icon={Clock}
                  label="Recent"
                  action={
                    <button
                      onClick={clearRecent}
                      className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      Clear
                    </button>
                  }
                />
                <div className="flex flex-wrap gap-2 px-4 pb-4">
                  {recent.map((r) => (
                    <Chip
                      key={r}
                      label={r}
                      onClick={() => onSelectSuggestion(r)}
                      onDelete={() => deleteRecent(r)}
                    />
                  ))}
                </div>
              </>
            )}

            {trending.length > 0 && (
              <>
                <SectionHeading icon={TrendingUp} label="Trending" />
                <div className="flex flex-wrap gap-2 px-4 pb-4">
                  {trending.slice(0, 8).map((t) => (
                    <Chip key={t} label={t} onClick={() => onSelectSuggestion(t)} />
                  ))}
                </div>
              </>
            )}

            {recent.length === 0 && trending.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 px-4">
                <Search className="w-10 h-10 text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">Start typing to search…</p>
              </div>
            )}
          </>
        )}

        {/* ── Loading ── */}
        {hasQuery && loading && <Skeleton />}

        {/* ── Error ── */}
        {hasQuery && !loading && error && (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <p className="text-sm text-gray-500 mb-3">{error}</p>
            <button
              onClick={retry}
              className="inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:underline"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
          </div>
        )}

        {/* ── Empty / zero-result state ── */}
        {showResults && !error && !hasResults && (
          <>
            {/* Did you mean */}
            <div className="px-4 pt-6 pb-4 text-center">
              <Search className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              {didYouMean ? (
                <p className="text-sm text-gray-500 leading-relaxed">
                  No results for{' '}
                  <strong className="text-gray-700">"{query}"</strong>.<br />
                  Did you mean{' '}
                  <button
                    onClick={() => onSelectSuggestion(didYouMean)}
                    className="font-semibold text-[#D4AF37] hover:underline"
                  >
                    {didYouMean}
                  </button>
                  ?
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  No results for{' '}
                  <strong className="text-gray-700">"{query}"</strong>
                </p>
              )}
            </div>

            {/* Similar products */}
            {similarProducts?.length > 0 && (
              <>
                <SectionHeading icon={Sparkles} label="You might like" />
                <div>
                  {similarProducts.map((p) => (
                    <ProductRow key={p.id} product={p} onClose={close} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── Results ── */}
        {showResults && !error && hasResults && (
          <>
            {/* Categories */}
            {categories.length > 0 && (
              <>
                <SectionHeading icon={Tag} label="Categories" />
                <div className="flex flex-wrap gap-2 px-4 pb-4">
                  {categories.map((cat) => (
                    <Chip key={cat} label={cat} onClick={() => onSelectSuggestion(cat)} />
                  ))}
                </div>
              </>
            )}

            {/* Keyword suggestions */}
            {suggestions.length > 0 && (
              <>
                <SectionHeading icon={Search} label="Suggestions" />
                <div className="pb-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => onSelectSuggestion(s)}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700
                                 hover:bg-[#FAF8F3] flex items-center gap-2 border-b border-gray-50
                                 last:border-0 transition-colors"
                    >
                      <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Products */}
            {products.length > 0 && (
              <>
                <SectionHeading icon={Search} label="Products" />
                <div>
                  {products.map((p) => (
                    <ProductRow key={p.id} product={p} onClose={close} />
                  ))}
                </div>
              </>
            )}

            {/* View all CTA */}
            <div className="px-4 py-5 border-t border-gray-100">
              <button
                onClick={onSubmit}
                className="w-full flex items-center justify-center gap-2 py-3
                           border border-[#D4AF37] text-[#D4AF37] text-sm font-semibold
                           rounded-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
              >
                View all results for "{query}"
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}